import {
  CHUNK_LOAD_RELOAD_KEY_PREFIX,
  MAX_CHUNK_LOAD_RELOADS_PER_VERSION,
} from "@/lib/errors/chunk-load-reload-guard/constants";

/**
 * Counts reloads for the current build in this tab when sessionStorage is
 * unavailable (Safari private mode, storage disabled). Module state dies with
 * the reload, so it only stops a double reload within one page lifetime.
 */
let _inMemoryReloadCount = 0;

/**
 * Reloads the page once per build to recover a tab running stale chunks, and
 * returns whether the reload was triggered.
 *
 * The counter is keyed by build version, so a later deploy gets its own
 * attempt while a still-broken build cannot loop. Callers should render their
 * error UI when this returns false.
 */
export function attemptChunkLoadReload(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  if (!_consumeReloadAttempt()) {
    return false;
  }

  window.location.reload();
  return true;
}

export function getChunkLoadReloadKey(): string {
  const version = process.env.NEXT_PUBLIC_VERSION ?? "unknown-version";
  return `${CHUNK_LOAD_RELOAD_KEY_PREFIX}:${version}`;
}

/** Exported for tests, which need a clean counter between cases. */
export function resetInMemoryChunkLoadReloadCount(): void {
  _inMemoryReloadCount = 0;
}

function _consumeReloadAttempt(): boolean {
  const key = getChunkLoadReloadKey();

  try {
    const stored = Number.parseInt(
      window.sessionStorage.getItem(key) ?? "0",
      10,
    );
    const attempts = Number.isNaN(stored) ? 0 : stored;

    if (attempts >= MAX_CHUNK_LOAD_RELOADS_PER_VERSION) {
      return false;
    }

    window.sessionStorage.setItem(key, `${attempts + 1}`);
    return true;
  } catch {
    if (_inMemoryReloadCount >= MAX_CHUNK_LOAD_RELOADS_PER_VERSION) {
      return false;
    }

    _inMemoryReloadCount += 1;
    return true;
  }
}
